// utils/formularioErrores.ts — qué hacer cuando un formulario NO pasa su validación al guardar (ronda F2, hallazgo G1).
//
// `form.validateFields()` rechaza con `{ errorFields: [{ name, errors }] }` y deja el mensaje de cada campo bajo el campo. Eso
// alcanza cuando el campo está a la vista, pero en el celular lo normal es que NO lo esté: «Guardar cambios» vive en una barra
// fija abajo y el teléfono está arriba, o el modal es más alto que la pantalla. Los formularios de teléfono que `return`aban en
// silencio dejaban a la persona viendo girar el botón y volver, sin un aviso, sin enviar nada y sin saber qué corregir (antes
// salía un aviso genérico; al quitarlo por «sobra encima» se perdió la única pista).
//
// Esta función hace las tres cosas: lleva la pantalla al primer campo con error, le da el foco (el lector de pantalla anuncia su
// mensaje) y deja un aviso breve. El «primer» campo es el primero del formulario, no el primero que terminó de validarse.
import type { FormInstance } from "antd";
import { getMsg } from "./antdStatic";

/** El aviso breve que acompaña al desplazamiento. Corto a propósito: el detalle ya está bajo el campo. */
export const AVISO_REVISA_LOS_CAMPOS = "Revisa los campos marcados en rojo antes de guardar.";

/**
 * Atributo que lleva toda barra que se queda fija sobre el contenido (hoy, «Tienes cambios sin guardar» de Mi perfil). El
 * desplazamiento lo busca para no dejar el campo con error DEBAJO de ella (ronda F3b).
 */
export const ATRIBUTO_BARRA_FIJA = "data-barra-fija";
const SELECTOR_BARRA_FIJA = `[${ATRIBUTO_BARRA_FIJA}]`;
/**
 * Cuánto aire debe quedar entre el campo y una barra fija que está más abajo. Alcanza para el mensaje rojo, que va DEBAJO del
 * control (~24 px) y su margen: con menos, el control se ve y su mensaje queda tapado.
 */
export const MARGEN_SOBRE_BARRA_FIJA_PX = 56;

type NombreDeCampo = string | number | (string | number)[];

/**
 * Si `control` quedó cerca de, o debajo de, una barra fija, lo centra en la pantalla (sin tocar el foco). `scrollToField` no alcanza: la
 * librería («scroll-into-view-if-needed», modo «si hace falta») da por bueno un campo que está dentro de la ventana aunque una barra
 * fija lo tape, así que en una pantalla de 1366x768 «Contraseña actual» quedaba bajo «Tienes cambios sin guardar» y la persona veía
 * el botón girar sin ver qué corregir.
 *
 * Mide el elemento real (en `llevarCampoALaVista`, el que SÍ tiene el foco porque `focusField` ya lo dejó ahí): la comprobación es del
 * resultado en pantalla, no una suposición sobre dónde quedó. Se exporta porque un campo que APARECE (la contraseña actual al cambiar
 * el correo) también puede nacer bajo la barra y hay que subirlo sin quitarle el foco a lo que la persona está escribiendo. Nunca
 * lanza: es una ayuda y el error ya está marcado bajo el campo.
 */
export function despejarDeBarraFija(control: Element | null): void {
  if (typeof document === "undefined") return;
  if (!(control instanceof HTMLElement) || control === document.body) return;
  const barras = Array.from(document.querySelectorAll<HTMLElement>(SELECTOR_BARRA_FIJA));
  if (barras.length === 0) return;
  const r = control.getBoundingClientRect();
  // Un campo que acaba de APARECER más abajo del borde de la ventana (en un celular, «Contraseña actual» nace fuera de la pantalla, a
  // 805 px en una de 740) tampoco se ve: se sube igual. Con el campo ya enfocado por `focusField` esto no ocurre (ya está dentro).
  if (window.innerHeight > 0 && (r.top < 0 || r.bottom > window.innerHeight)) {
    control.scrollIntoView?.({ block: "center" });
    return;
  }
  for (const barra of barras) {
    // El botón «Guardar» vive dentro de la barra: si el foco quedó ahí (no se pudo dar al campo), no hay nada que despejar.
    if (barra.contains(control)) continue;
    const b = barra.getBoundingClientRect();
    if (b.width === 0 && b.height === 0) continue; // oculta: no tapa nada
    const seCruzanEnHorizontal = r.left < b.right && r.right > b.left;
    const quedaCercaODebajo = r.bottom + MARGEN_SOBRE_BARRA_FIJA_PX > b.top && r.top < b.bottom;
    if (seCruzanEnHorizontal && quedaCercaODebajo) {
      control.scrollIntoView?.({ block: "center" });
      return;
    }
  }
}

/**
 * Lleva a la persona hasta el campo `campo`: desplaza la pantalla hasta él (centrado), le da el foco (el lector de pantalla anuncia su
 * mensaje) y, si una barra fija lo tapa, lo sube por encima de ella. Es la ÚNICA puerta para «ir a un campo con error»: los avisos de
 * validación, del teléfono y de la contraseña actual pasan todos por acá, así ninguno se olvida de la barra.
 *
 * El desplazamiento, el foco y el despeje van en `try` SEPARADOS (ronda F3, H9): si el desplazamiento lanza (un navegador sin
 * `Element.scroll`, que `scroll-into-view-if-needed` usa), el foco igual se da; con todo en el mismo `try` el primer fallo se
 * llevaba a los demás. Nunca lanza.
 */
export function llevarCampoALaVista(form: FormInstance, campo: NombreDeCampo): void {
  try {
    form.scrollToField(campo, { block: "center" });
  } catch {
    /* sin desplazamiento: el error sigue marcado en el campo y el foco lo lleva igual hasta él */
  }
  try {
    form.focusField(campo);
  } catch {
    /* sin foco: el error sigue marcado en el campo */
  }
  try {
    despejarDeBarraFija(typeof document === "undefined" ? null : document.activeElement);
  } catch {
    /* sin despeje: el campo igual quedó marcado y enfocado */
  }
}

/** El primer campo con error de lo que rechazó `validateFields()`, o `null` si el error no es de validación. */
export function primerCampoConError(error: unknown): NombreDeCampo | null {
  const campos = (error as { errorFields?: { name?: unknown }[] } | null | undefined)?.errorFields;
  if (!Array.isArray(campos) || campos.length === 0) return null;
  const nombre = campos[0]?.name;
  return Array.isArray(nombre) || typeof nombre === "string" || typeof nombre === "number" ? (nombre as NombreDeCampo) : null;
}

/**
 * Lleva a la persona al primer campo con error de un `validateFields()` fallido: desplaza la pantalla hasta él (centrado, para que
 * la barra fija de abajo no lo tape), le da el foco y avisa. Devuelve `true` si el error era de validación.
 *
 * - `avisar: false` cuando la pantalla ya muestra su propio aviso (p. ej. un diálogo con la lista de campos).
 * - Nunca lanza: el desplazamiento y el foco son una ayuda; el error ya quedó marcado bajo el campo (ver `llevarCampoALaVista`).
 */
export function irAlPrimerError(form: FormInstance, error: unknown, opciones: { avisar?: boolean } = {}): boolean {
  const campo = primerCampoConError(error);
  if (campo === null) return false;
  llevarCampoALaVista(form, campo);
  if (opciones.avisar !== false) getMsg()?.warning(AVISO_REVISA_LOS_CAMPOS);
  return true;
}
